import { FC, ReactNode, useEffect, useMemo, useState } from 'react';
import {
  BoxV2 as Box,
  Flex,
  Grid,
  Tooltip,
  usePermission,
} from 'portal-commons';

import { CustomSwitch } from '@/shared_elements';
import StatusIndicator from './StatusIndicator';
import { RcsCampaign } from '../../types';
import { convertTimeWithTimezone } from '@/utils/time';
import { snakeToTitleCase } from '@/utils';
import { selectDynamicContent } from '@/applications/CspPortal/CSP-Form/selectors';
import { useSelector } from 'react-redux';
import ConfirmationDialog from '../ConfirmationDialog';

const InfoItem: FC<{
  label: string;
  sx?: Record<string, unknown>;
  valueSx?: Record<string, unknown>;
}> = ({ label, children, sx, valueSx }) => {
  return (
    <Box
      sx={{
        fontSize: 'H400',
        color: 't.black80',
        textAlign: 'left',
        '& > label': { mr: 'xxs' },
        '& > span': { fontWeight: 'semiBold', ...valueSx },
        ...sx,
      }}
    >
      <label>{label}:</label>
      <span>{children}</span>
    </Box>
  );
};
const Column: FC<{ children: ReactNode; sx?: Record<string, unknown> }> = ({
  children,
  sx,
}) => {
  return (
    <Flex
      sx={{
        flexDirection: 'column',
        gap: '7px',
        width: 'max-content',
        maxWidth: '100%',
        alignItems: 'flex-start',
        ...sx,
      }}
    >
      {children}
    </Flex>
  );
};

interface BasicInfoSectionProps {
  campaign?: RcsCampaign;
  type: 'csp' | 'cnp';
  onUpdate?: (data: Partial<RcsCampaign>) => void;
}

const BasicInfoSection: FC<BasicInfoSectionProps> = ({
  campaign,
  type,
  onUpdate,
}) => {
  if (type === 'csp') {
    return <CspSummary campaign={campaign} onUpdate={onUpdate} />;
  }
  return <CnpSummary campaign={campaign} />;
};

const CspSummary = ({
  campaign,
  onUpdate,
}: {
  campaign?: RcsCampaign;
  onUpdate?: (data: Partial<RcsCampaign>) => void;
}) => {
  const dynamicContent = useSelector(selectDynamicContent);
  const [renewalData, setRenewalData] = useState<string>('');
  const [openModal, setOpenModal] = useState(false);
  const { hasPermission } = usePermission();

  const isExpired = useMemo(
    () => campaign?.status === 'EXPIRED',
    [campaign?.status]
  );

  useEffect(() => {
    if (dynamicContent.length && renewalData === '') {
      const data = dynamicContent.find(
        (item) =>
          item.lookup_key === 'csp-campaign-dialog-confirm#cancel-auto-renewal'
      );
      data && setRenewalData(data.text);
    }
  }, [dynamicContent, renewalData]);

  const handleAutoRenewalChange = () => {
    if (!campaign) return;
    if (campaign.autoRenewal) {
      setOpenModal(true);
    } else {
      onUpdate?.({ autoRenewal: true });
    }
  };

  return (
    <Grid
      as="section"
      data-testid="basicInfoSection"
      sx={{
        gridTemplateColumns: '50% 1px 50%',
        marginTop: 'xxl',
        marginBottom: 'xl',
        justifyItems: 'anchor-center',
        padding: '0 8%',
      }}
    >
      <Column>
        <InfoItem
          label="Brand DBA Name"
          sx={{ a: { color: 't.blue100' } }}
          valueSx={{ wordBreak: 'break-all' }}
        >
          <a
            data-testid="campaignDetailBasicBrandNameButton"
            href={`/brands/${campaign?.brandUid}`}
          >
            {campaign?.brandName}
          </a>
        </InfoItem>
        <InfoItem label="Use-Case">{campaign?.usecase}</InfoItem>
        <InfoItem label="Billing Category">
          {snakeToTitleCase(campaign?.billingCategory)}
        </InfoItem>
      </Column>
      <Divider type="csp" />
      <Column>
        <InfoItem label="TCR Status">
          {campaign?.status && (
            <StatusIndicator
              variant={
                campaign?.status?.toLocaleLowerCase() === 'active'
                  ? 'success'
                  : 'error'
              }
            />
          )}
          {campaign?.status}
        </InfoItem>
        <InfoItem label="Registered" valueSx={{ color: 't.black60' }}>
          {convertTimeWithTimezone(
            campaign?.createDate,
            undefined,
            'MM/DD/YYYY'
          )}
        </InfoItem>
        <InfoItem
          label={isExpired || !campaign?.autoRenewal ? 'Expiration' : 'Renewal'}
          valueSx={{ color: 't.black60' }}
        >
          {convertTimeWithTimezone(
            campaign?.nextRenewalOrExpirationDate,
            undefined,
            'MM/DD/YYYY'
          )}
        </InfoItem>
        {!isExpired && (
          <Flex sx={{ flexDirection: 'row', alignItems: 'center' }}>
            <CustomSwitch
              val={campaign?.autoRenewal}
              disabled={!hasPermission('rcsCampaignDetail.autoRenewalToggle')}
              handleChange={handleAutoRenewalChange}
            />
            <Box
              as="label"
              sx={{ fontSize: 'H400', color: 't.black80', display: 'block' }}
            >
              Auto Renewal
            </Box>
            <div>
              <Tooltip
                title="If set to “No” , the campaign will be deactivated at the end of the active period."
                placement="right-end"
                style={{
                  bottom: '2px',
                  right: '-8px',
                  top: undefined,
                  position: 'relative',
                  display: 'block',
                }}
              />
            </div>
          </Flex>
        )}
      </Column>
      <ConfirmationDialog
        open={openModal}
        type="renewal"
        text={renewalData}
        handleCancel={() => setOpenModal(false)}
        handleSuccess={() => {
          setOpenModal(false);
          onUpdate?.({ autoRenewal: false });
        }}
      />
    </Grid>
  );
};

const Divider = ({ type }: { type: 'csp' | 'cnp' }) => {
  if (type === 'csp') {
    return <Box sx={{ width: '100%', height: '80%', background: '#EDEEEF' }} />;
  }
  return (
    <Box
      sx={{
        width: '100%',
        height: '200%',
        background: '#EDEEEF',
        position: 'relative',
        top: '-20px',
      }}
    />
  );
};

const CnpSummary = ({ campaign }: { campaign?: RcsCampaign }) => {
  return (
    <Grid
      as="section"
      data-testid="basicInfoSection"
      sx={{
        gridTemplateColumns: '32% 1px 35% 1px 32%',
        marginTop: 'xxxxl',
        marginBottom: 'xxxxl',
        justifyItems: 'anchor-center',
        padding: '0 4%',
      }}
    >
      <Column sx={{ width: '100%' }}>
        <InfoItem label="Use-Case">{campaign?.usecase}</InfoItem>
        <InfoItem label="Billing Category">
          {snakeToTitleCase(campaign?.billingCategory)}
        </InfoItem>
      </Column>
      <Divider type="cnp" />
      <Column>
        <InfoItem label="TCR Status">
          {campaign?.status && (
            <StatusIndicator
              variant={
                campaign?.status?.toLocaleLowerCase() === 'active'
                  ? 'success'
                  : 'error'
              }
            />
          )}
          {campaign?.status}
        </InfoItem>
        {/* <InfoItem label="Verification Status">
          {campaign?.verificationStatus && (
            <StatusIndicator
              variant={
                campaign?.verificationStatus?.toLocaleLowerCase() === 'verified'
                  ? 'success'
                  : 'error'
              }
            />
          )}
          {snakeToTitleCase(campaign?.verificationStatus)}
        </InfoItem> */}
      </Column>
      <Divider type="cnp" />
      <Column sx={{ width: '100%', alignItems: 'flex-end' }}>
        <InfoItem label="Registered On">
          {convertTimeWithTimezone(
            campaign?.createDate,
            undefined,
            'MM/DD/YYYY'
          )}
        </InfoItem>
      </Column>
    </Grid>
  );
};

export default BasicInfoSection;
